import L, {LatLng, LatLngExpression, Map} from 'leaflet';
import {Fragment} from 'react';
import {Polyline, useMap} from 'react-leaflet';

import {RotatedMarker} from './RotatedMarker';

interface ArrowLineProps {
  start: LatLngExpression;
  end: LatLngExpression;
  icon: L.Icon;
  color?: string;
  verticalOffset?: number;
  short?: number;
  minLength?: number;
  dashed?: boolean;
  shift?: number;
}

const calculateRotationAngle = (start: LatLng, end: LatLng): number => {
  const dx = end.lng - start.lng;
  const dy = end.lat - start.lat;
  const angleRad = Math.atan2(dx, dy);
  const angleDeg = (angleRad * 180) / Math.PI;

  return angleDeg;
};

const shiftUp = (
  map: L.Map,
  latlng: LatLngExpression,
  offsetPx: number
): LatLng => {
  const point = map.latLngToContainerPoint(latlng);
  const shiftedPoint = L.point(point.x, point.y - offsetPx);
  return map.containerPointToLatLng(shiftedPoint);
};

const shortenLine = (
  map: L.Map,
  start: LatLng,
  end: LatLng,
  shortenStart: number,
  shortenEnd: number,
  minLength: number
): [LatLng, LatLng] | null => {
  const p1 = map.latLngToContainerPoint(start);
  const p2 = map.latLngToContainerPoint(end);

  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length < shortenStart + shortenEnd + minLength) {
    return null;
  }

  const factorStart = shortenStart / length;
  const factorEnd = shortenEnd / length;

  const newP1 = L.point(p1.x + dx * factorStart, p1.y + dy * factorStart);
  const newP2 = L.point(p2.x - dx * factorEnd, p2.y - dy * factorEnd);

  return [map.containerPointToLatLng(newP1), map.containerPointToLatLng(newP2)];
};

export const shiftLineNormally = (
  map: Map,
  start: LatLng,
  end: LatLng,
  pixelShift: number
): [LatLng, LatLng] => {
  const startPoint = map.latLngToContainerPoint(start);
  const endPoint = map.latLngToContainerPoint(end);

  const dx = endPoint.x - startPoint.x;
  const dy = endPoint.y - startPoint.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) {
    return [start, end];
  }

  const offsetX = (-dy / length) * pixelShift;
  const offsetY = (dx / length) * pixelShift;

  const newStart = map.containerPointToLatLng(
    L.point(startPoint.x + offsetX, startPoint.y + offsetY)
  );

  const newEnd = map.containerPointToLatLng(
    L.point(endPoint.x + offsetX, endPoint.y + offsetY)
  );

  return [newStart, newEnd];
};

export const ArrowLine = ({
  start,
  end,
  icon,
  color = 'black',
  verticalOffset = 16,
  short = 22,
  minLength = 0,
  dashed = false,
  shift = 0,
}: ArrowLineProps): JSX.Element | null => {
  const map = useMap();

  const adjustedStart = shiftUp(map, start, verticalOffset);
  const adjustedEnd = shiftUp(map, end, verticalOffset);

  let [s, e] = [adjustedStart, adjustedEnd];

  if (shift !== 0) {
    [s, e] = shiftLineNormally(map, adjustedStart, adjustedEnd, shift);
  }

  const shortened = shortenLine(map, s, e, short, short + 5, minLength);

  if (!shortened) {
    return null;
  }

  const [newStart, newEnd] = shortened;
  const angle = calculateRotationAngle(newStart, newEnd);

  return (
    <Fragment>
      <Polyline
        key={`polyline-${color}`}
        positions={[
          [newStart.lat, newStart.lng],
          [newEnd.lat, newEnd.lng],
        ]}
        color={color}
        weight={3}
        pathOptions={{
          lineCap: 'butt',
          weight: 3,
          dashArray: dashed ? '5' : '1 0',
        }}
      />
      <RotatedMarker
        position={[newEnd.lat, newEnd.lng]}
        icon={icon}
        rotationAngle={angle}
        rotationOrigin="center center"
      />
    </Fragment>
  );
};
